import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import * as SvgIcons from 'assets/svg/sprite'
import { IconPauseFill, IconPlayOutline } from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import BackgroundPicker, {
    getStoryBackground
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Sidebar from 'components/UI/Sidebar/Sidebar'
import SidebarItem from 'components/UI/Sidebar/SidebarItem'
import {
  ScheduledInfoBubble,
  ShareStory,
  StoryAddPoll,
  UploadStoryMedia
} from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import DraggableTextContainer from 'features/PhotoEdit/DraggableText/DraggableTextContainer'
import ProcessingFile from 'features/ProcessingFile/ProcessingFile'
import { AllIcons } from 'helpers/allIcons'
import { useCreateStory } from 'services/stories'
import { useUserProfile } from 'store'

import { PollPreview } from '../newPost/components/PollPreview'


export const NewStory = () => {
  const [storyHeight, setStoryHeight] = useState<string>('100dvh')

  useEffect(() => {
      const updateHeight = () => {
          const vv = window.visualViewport

          const height = vv?.height ?? window.innerHeight

          setStoryHeight(`${Math.round(height)}px`)
      }

      updateHeight()

      window.visualViewport?.addEventListener('resize', updateHeight)
      window.visualViewport?.addEventListener('scroll', updateHeight)
      window.addEventListener('resize', updateHeight)

      return () => {
          window.visualViewport?.removeEventListener('resize', updateHeight)
          window.visualViewport?.removeEventListener('scroll', updateHeight)
          window.removeEventListener('resize', updateHeight)
      }
  }, [])
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const { t } = useTranslation()
  const {
    background,
    image,
    addPollOpen,
    setAddPollOpen,
    video,
    isAddTextOpen,
    initialPosition,
    initialText,
    handleSetCoordinates,
    pollData,
    handleRemoveText,
    setBackground,
    isTextDragged,
    textValue,
    setTextValue,
    setIsTextDragged,
    scheduleDate,
    handleSetImageData,
    handleSetVideoData,
    formatedStory,
    handleUploadAbort,
    handleSettingsClick,
    handleCloseClick,
    handleSetProgressInfo,
    toggleDateModal,
    uploadProgress,
    handleSetPollFunction,
    handleRemoveSchedule,
    handleClearPollData,
    handleOpenPoll,
    handleOpenText,
    clearMedia,
    mediaType,
    setMediaType
  } = useCreateStory()

  const hasVideo = Boolean(video?.length)
  const hasImage = Boolean(image)
  const hasMedia = hasVideo || hasImage
  const [lastPct, setLastPct] = useState(0)
  const userData = useUserProfile()
  const modalData = useModalContext()
  const [mediaUploadStarted, setMediaUploadStarted] = useState(false)

  const shareDisabled = useMemo(() => {
    if (!hasMedia) return false

    const firstVideo = video?.[0]

    const isUploading =
      uploadProgress !== null &&
      typeof uploadProgress?.percentage === 'number' &&
      uploadProgress.percentage < 100

    if (isUploading) {
      return true
    }

    if (hasVideo) {
      return !firstVideo?.id
    }

    if (hasImage) {
      return !image?.id
    }

    return false
  }, [
    hasMedia,
    hasVideo,
    hasImage,
    video,
    image?.id,
    uploadProgress
  ])

    // const payoutDisabled = userData?.payout_method == null
    const payoutDisabled =
        userData?.role === 'model' &&
        userData?.payout_method == null
    const payoutModalOpenedRef = useRef(false)
    const navigate = useNavigate()

const videoSrc = useMemo(() => {
  return video?.[0]?.preview_path ?? ''
}, [video])

const videoUploadFinished =
  hasVideo &&
  !!video?.[0]?.id

useEffect(() => {
  const p = uploadProgress?.percentage
  if (typeof p === 'number' && p >= 0 && p <= 100) {
    setLastPct(prev => (p >= prev ? p : prev))
  }
}, [uploadProgress?.percentage])
  
  const blurBgSrc = useMemo(() => {
    const src = thumbnail ?? image?.path ?? null
    return src
  }, [thumbnail, image?.path])

  // When the video URL changes, we expect to re-arm the player
  useEffect(() => {
    setIsVideoPlaying(false)
  }, [videoSrc])


  const normalizedBackground = useMemo(() => {
      return getStoryBackground(background)
  }, [background])

  const handleVideoPlay = async () => {
    const element = videoRef.current
    if (!element) return

    if (!element.paused) {
      element.pause()
      return
    }

    try {
      await element.play()
    } catch (error) {
      console.error('[NewStory] Blob playback failed:', error)
    }
  }
   useEffect(() => {
    if (!userData) return

    if (payoutDisabled && !payoutModalOpenedRef.current) {
        payoutModalOpenedRef.current = true

        modalData.addModal(
            t('payoutRequired'),
            <ConfirmModal
                confirmButtonWidth="14"
                body={t('payoutRequiredDescription')}
                confirmText={t('gotoSettings')}
                confirmFn={() => {
                    navigate('/settings/general/payout-settings')
                }}
            />
        )
    }
    }, [userData, payoutDisabled, modalData, t])


  return (
    <StoryWrapper
        style={
            window.innerWidth < 768
                ? { height: storyHeight }
                : undefined
        }
    >
      {!addPollOpen && <Title>{t('addStory')}</Title>}

      {/* BACKGROUND ONLY (no image/video) */}
      {Boolean(normalizedBackground && !image && !video) && (
        <MediaContainer
          style={{
            backgroundImage: normalizedBackground ?? undefined
          }}
        />
      )}

      {/* IMAGE */}
      {Boolean(image) && (
        <MediaWrapper
          style={{
            backgroundImage: `url(${image?.path})`
          }}
        >
          {/* Foreground image container (contain) */}
          <MediaContainer
            style={{
              backgroundImage: `url(${image?.path})`
            }}
          />
        </MediaWrapper>
      )}

      {/* VIDEO */}
      {Boolean(video?.length) && (
        <MediaWrapper style={{ backgroundColor: 'black' }}>
          <MediaContainer
            style={{
              position: 'relative',
              // don't blur the container holding the video on iOS
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none'
            }}
          >
            {/* BLURRED BACKGROUND (under everything) */}
            {blurBgSrc && (
              <div
                className="preview__background"
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${blurBgSrc})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(20px) brightness(0.84)',
                  transform: 'scale(1.02)',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              />
            )}

            {/* THUMBNAIL OVERLAY (centered; disappears when player starts) */}
{/* Show thumbnail while upload is not finished */}
{(!videoUploadFinished || !videoSrc) && thumbnail && (
  <StoryThumbnail>
    <img src={thumbnail} alt="Video thumbnail" />
  </StoryThumbnail>
)}

{/* Simple local blob preview */}
{videoUploadFinished && videoSrc && (
  <StoryVideoContainer>
    <video
      ref={videoRef}
      key={videoSrc}
      src={videoSrc}
      poster={thumbnail ?? undefined}
      playsInline
      preload="metadata"
      onClick={handleVideoPlay}
      onPlay={() => setIsVideoPlaying(true)}
      onPause={() => setIsVideoPlaying(false)}
      onEnded={() => {
        const element = videoRef.current

        if (element) {
          element.currentTime = 0
        }

        setIsVideoPlaying(false)
      }}
    />

    {!isVideoPlaying && (
      <StoryVideoPlayButton
        type="button"
        aria-label="Play video"
        onClick={handleVideoPlay}
      >
        <IconPlayOutline color="#fff" />
      </StoryVideoPlayButton>
    )}
  </StoryVideoContainer>
)}
          </MediaContainer>
        </MediaWrapper>
      )}

      {(textValue || isAddTextOpen) && (
        <TextContainer>
          <DraggableTextContainer
            customClass="photoEdit__container__text"
            initialValue={initialText}
            initialX={initialPosition[0]}
            initialY={initialPosition[1]}
            isEditable={true}
            isOpen={isAddTextOpen}
            isDragging={isTextDragged}
            setValue={(v) => {
              setTextValue(v)
            }}
            setCoordinates={(x, y) => {
              handleSetCoordinates(x, y)
            }}
            setIsDragging={(d) => {
              setIsTextDragged(d)
            }}
            onRemove={() => {
              handleRemoveText()
            }}
          />
        </TextContainer>
      )}

      {pollData && (
        <PollContainer>
          <PollPreview
            pollData={pollData}
            hideBackground
            clearPollData={() => {
              handleClearPollData()
            }}
          />
        </PollContainer>
      )}

      <SettingsButtonWrapper>
        <IconButton
          desc={t('settings')}
          type="transparent"
          size="small"
          icon={<SvgIcons.IconSettingsOutline />}
          clickFn={() => {
            handleSettingsClick()
          }}
        />
      </SettingsButtonWrapper>

      {!addPollOpen && !hasVideo && (
        <StorySidebar>
          <Sidebar>
            <SidebarItem
              icon={<SvgIcons.IconText color="white" />}
              text={t('text')}
              isDisabled={isAddTextOpen}
              onClick={() => {
                handleOpenText()
              }}
            />
          {!hasMedia && (
            <BackgroundPicker
              background={background}
              setBackground={(bg) => {
                setBackground(bg)
              }}
            />
          )}
            {!pollData && (
            <SidebarItem
              icon={<SvgIcons.IconPoll color="white" />}
              text={t('poll')}
              isDisabled={hasVideo}
              onClick={() => {
                if (hasVideo) return
                handleOpenPoll()
              }}
            />
          )}
          </Sidebar>
        </StorySidebar>
      )}

      <CloseButtonWrapper>
        <IconButton
          desc={t('close')}
          type="transparent"
          size="small"
          clickFn={() => {
            handleCloseClick()
          }}
          icon={AllIcons.close}
        />
      </CloseButtonWrapper>

      <FooterContainer>
        {scheduleDate && (
          <UpperFooter>
            <ScheduledInfoBubble
              date={scheduleDate}
              removeSchedule={() => {
                handleRemoveSchedule()
              }}
            />
          </UpperFooter>
        )}
        <LowerFooter>
          <UploadStoryMedia
              disabled={payoutDisabled}
              setImageData={(id, path, orientation, exif_data) => {
                  handleSetImageData(id, path, orientation, exif_data)
              }}
            setVideoData={(data) => {
              setMediaUploadStarted(true)
              handleSetVideoData(data)
            }}
            setProgressInfo={(p) => {
              if (p !== null) {
                setMediaUploadStarted(true)
              }

              if (p === 100) {
                setMediaUploadStarted(false)
              }

              handleSetProgressInfo(p)
            }}
            clearMedia={() => {
              setMediaUploadStarted(false)
              clearMedia()
            }}
            setMediaType={(t) => {
              if (t?.type?.startsWith('video/')) {
                setMediaUploadStarted(true)
              }
              setMediaType(t)
            }}
            setThumbnail={(t) => {
              setThumbnail(t)
            }}
            onUploadError={(message) => {
            modalData.addModal(
              t('uploadError', {
                defaultValue: 'Upload Error',
              }),
              <ConfirmModal
                body={message}
                confirmText={t('ok', {
                  defaultValue: 'OK',
                })}
                confirmFn={() => {}}
                hideCancel
              />
            )
          }}
          />
          <ShareStory
            story={formatedStory}
            disabled={shareDisabled}
            onSuccess={() => {
            navigate(`/${userData?.username}`, {
              state: {
                skipProfileFeedRefetch: true
              }
            })
          }}
          />
          <FooterButton
            clickFn={() => {
              toggleDateModal()
            }}
            icon={
              <SvgIcons.IconCalendarClockOutline2
                color={scheduleDate ? '#2894FF' : 'white'}
              />
            }
            type="black--transparent"
          />
        </LowerFooter>
      </FooterContainer>

      {hasMedia && (shareDisabled || mediaUploadStarted) && (
        <UploadProgressContainer
          percentage={lastPct || 1}
          onAbort={handleUploadAbort}
          text={mediaType?.type?.includes('video') ? 'Uploading' : ''}
          hideAbort
        />
      )}

      <StoryAddPoll
        open={addPollOpen}
        setOpen={(o) => {
          setAddPollOpen(o)
        }}
        setPollDataFunction={(f) => {
          handleSetPollFunction(f)
        }}
        background={background}
      />
    </StoryWrapper>
  )
}

/* ----------------- styled components (unchanged except where noted) ----------------- */
const FooterContainer = styled.div`
  width: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  margin: 0 20px 2.7rem;
  z-index: 1;
  pointer-events: auto;
`

const UpperFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const LowerFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 1;
  @media (min-width: 768px) {
  justify-content: center;
  gap: 40px;
}
`

const StoryThumbnail = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
  pointer-events: none;

  img {
    display: block;

    width: auto;
    height: auto;

    max-width: 100%;
    max-height: 100%;

    object-fit: contain;
    object-position: center;

    border-radius: 8px;
  }
`

const StoryVideoContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
  background: transparent;

  video {
    display: block;

    width: 100%;
    height: 100%;

    max-width: 100%;
    max-height: 100%;

    object-fit: contain;
    object-position: center;

    background: transparent;
    cursor: pointer;
  }
`

const StoryVideoPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;

  transform: translate(-50%, -50%);

  display: flex;
  align-items: center;
  justify-content: center;

  width: 7rem;
  height: 7rem;

  padding: 0;
  border: none;
  border-radius: 0;
  background: none;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;

  color: white;
  cursor: pointer;

  svg {
    width: 3.5rem;
    height: 3.5rem;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.08);
  }

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 4px;
  }
`
const SettingsButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  left: 2rem;

  background: none;
  border: none;

  padding: 0;
`

const PollContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  width: 100%;
  display: flex;
  justify-content: center;

  z-index: 1;
`

const StorySidebar = styled.div`
  position: absolute;
  top: 9rem;
  left: 1rem;
  z-index: 2;
`

const TextContainer = styled.div`
  height: 100%;
  width: 100%;

  position: absolute;
  top: 0;
  left: 0;

  overflow: hidden;
`

const MediaWrapper = styled.div`
  height: 100vh;
  width: 100vw;

  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (min-width: 768px) {
    height: 100%;
    width: 100%;
    border-radius:2.5rem;
  }
`

const MediaContainer = styled.div`
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;

    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;

    backdrop-filter: blur(19px) brightness(0.8);
    -webkit-backdrop-filter: blur(19px) brightness(0.8);

    @media (min-width: 768px) {
        border-radius: 2.5rem;
    }
`

const StoryWrapper = styled.div`
  height: 100dvh;
  position: relative;

  @media (min-width: 768px) {
    width: 100%;

    // 7rem header + 2rem bottom gap
    height: calc(100dvh - 9rem);

    border-radius: 2.5rem;
    overflow: hidden;
  }
`
const Title = styled.div`
  position: absolute;
  left: 50%;
  z-index: 3;

  font-family: 'Montserrat', sans-serif;
  color: white;
  font-style: normal;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.02em;

  transform: translate(-50%);

  margin-top: 26px;
`


type FooterButtonProps = {
  disabled?: boolean
}

export const FooterButton = styled(IconButton)<FooterButtonProps>`
  border: none;
  border-radius: 2rem;

  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`

const UploadProgressContainer = styled(ProcessingFile)`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;

  transform: translate(-50%, -50%);

  width: 12rem;
  height: auto;

  display: flex;
  flex-direction: column;
  align-items: center;

  .processingFile__wrapper {
    width: 100%;
    height: auto;
    padding: 0;

    background: none !important;
    opacity: 0.8 !important;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .processingFile__progress {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;

    width: 100%;
    padding: 0;
    margin-top: 1rem;

    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .processingFile__progress--bar {
    width: 100%;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  right: 2rem;
  z-index: 3;
    @media (min-width: 800px) {
    display: none;
  }
  .iconbutton img {
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }

`
